'use client'

import { ArrowUpRight } from 'lucide-react'
import { BodyText, LabelText } from '@/components/ui/typography'
import { Link } from '@/i18n/routing'
import { getMoyuLocale } from '@/lib/moyu'
import { useLocale, useTranslations } from 'next-intl'

const linkClass =
  'inline-flex items-center gap-1 rounded-sm py-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'

export function Footer() {
  const t = useTranslations('footer')
  const locale = useLocale()
  const resourceLocale = getMoyuLocale(locale)
  const year = new Date().getFullYear()

  return (
    <footer className="bg-background/90 border-t">
      <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="grid gap-8 lg:grid-cols-[1fr_2fr] lg:gap-16">
          <div>
            <Link
              href="/"
              className="focus-visible:outline-ring rounded-sm text-lg font-semibold focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              Miaozi
            </Link>
            <BodyText className="text-muted-foreground mt-2 max-w-xs text-sm">
              {t('tagline')}
            </BodyText>
          </div>
          <nav
            className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3"
            aria-label={t('navigation')}
          >
            <div>
              <h2 className="mb-3 text-sm font-semibold">{t('learn')}</h2>
              <ul className="space-y-1 text-sm">
                {(
                  [
                    ['/read', 'stories'],
                    ['/dict', 'dictionary'],
                    ['/collections', 'flashcards'],
                    ['/blog', 'blog']
                  ] as const
                ).map(([href, key]) => (
                  <li key={key}>
                    <Link className={linkClass} href={href}>
                      {t(key)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="mb-3 text-sm font-semibold">{t('studyPaths')}</h2>
              <ul className="space-y-1 text-sm">
                {(
                  [
                    ['/hsk', 'hsk'],
                    ['/tocfl', 'tocfl'],
                    ['/pinyin', 'pinyin'],
                    ['/zhuyin', 'zhuyin']
                  ] as const
                ).map(([href, key]) => (
                  <li key={key}>
                    <Link className={linkClass} href={href}>
                      {t(key)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <h2 className="mb-3 text-sm font-semibold">
                {t('moreResources')}
              </h2>
              <ul className="space-y-4 text-sm">
                <li>
                  <a
                    className={linkClass}
                    href={`https://moyuchinese.com/${resourceLocale}`}
                  >
                    <span className="text-foreground font-medium">
                      Moyu Chinese
                    </span>
                    <ArrowUpRight
                      className="size-3.5 shrink-0"
                      aria-hidden="true"
                    />
                  </a>
                  <p className="text-muted-foreground max-w-xs text-xs leading-relaxed">
                    {t('moyuDescription')}
                  </p>
                </li>
                <li>
                  <a
                    className={linkClass}
                    href={`https://arel.dev/${resourceLocale}/`}
                  >
                    arel.dev
                    <ArrowUpRight
                      className="size-3.5 shrink-0"
                      aria-hidden="true"
                    />
                  </a>
                  <p className="text-muted-foreground max-w-xs text-xs leading-relaxed">
                    {t('arelDescription')}
                  </p>
                </li>
              </ul>
            </div>
          </nav>
        </div>
        <div className="mt-8 flex flex-col gap-4 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
          <LabelText className="text-muted-foreground text-xs">
            {t('copyright', { year })}
          </LabelText>
          <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
            {(
              [
                ['/about', 'about'],
                ['/contact', 'contact'],
                ['/settings', 'settings'],
                ['/privacy', 'privacy'],
                ['/terms', 'terms']
              ] as const
            ).map(([href, key]) => (
              <li key={key}>
                <Link className={linkClass} href={href}>
                  {t(key)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  )
}
